// Wire types for the Federation Providers settings feature (E12 F12-5, WI 633-637). Mirror the backend
// contracts: the AO settings-list (Models.Federation.FederationProviderListItemResponse + its domains),
// the provider-type lookup (FederationProviderTypeResponse), the wizard create payload
// (InsertFederationProviderRequest + FederationProviderDomainHint) and the edit-domain requests. Property
// names are the camelCased server DTO fields.

import { VerifiedDomainStatus } from '../VerifiedDomains/types'

export interface FederationProviderDomain {
  id: number
  domainName: string
  // Null on a grandfathered domain not yet linked to an E3 Verified domain — surfaced as the caution
  // badge + relink action (WI 636/637). Non-null once linked.
  verifiedDomainId: number | null
}

export interface FederationProviderListItem {
  id: number
  name: string
  providerType: string
  providerTypeId: number
  disabled: boolean
  // Set when a Graph register/update failed and the provider was auto-disabled (compensation path).
  lastFailureReason: string | null
  lastUpdate: string
  domains: FederationProviderDomain[]
}

// Display data only — nothing keys behaviour off id or name, which the seed scripts make non-contractual.
export interface FederationProviderType {
  id: number
  name: string
  protocolId: number
  protocolName: string
}

// One email domain routed to the provider, paired with the Verified domain that proves ownership. Both
// are required by the server (the TVP column + the insert sproc's D50 gate reject an unverified domain).
export interface FederationDomainHint {
  domainName: string
  verifiedDomainId: number
}

export interface CreateFederationProviderRequest {
  federationProviderTypeId: number
  name: string
  customerId?: number
  // The Completed consent state this provider binds to (400 federation_consent_state_invalid without it);
  // the issuer and the Entra tenant come off that state server-side, never the wire.
  consentStateKey: string
  domainHints: FederationDomainHint[]
}

export interface AddFederationDomainRequest {
  domainName: string
  verifiedDomainId: number
}

// --- E20 F20-7 (WI 794) — the Microsoft admin-consent round trip (Models.Federation wire DTOs) ---

export interface CreateFederationConsentUrlRequest {
  customerId?: number
  // Captured onto the consent-state row now; the poll never returns it (see the wizard's stash).
  name: string
}

export interface CreateFederationConsentUrlResponse {
  consentUrl: string
  // Opaque poll handle — deliberately NOT the single-use state token embedded in consentUrl.
  consentStateKey: string
  expiresAt: string
}

// Values start at 1 so a default-constructed response reads as 0 — an unnamed, non-success sentinel.
export enum FederationConsentStatus {
  Pending = 1,
  Completed = 2,
  Declined = 3,
  Expired = 4,
  Failed = 5,
}

// One Entra-provenance domain written during the consent leg, with the row's current status. Consent
// auto-verifies new rows as Verified, even when another LogikSpark tenant holds the same domain.
export interface FederationConsentDomain {
  verifiedDomainId: number
  domainName: string
  status: VerifiedDomainStatus
}

export interface FederationConsentState {
  status: FederationConsentStatus
  // Null until the provider exists — creation is deferred to the domain-link step.
  federationProviderId: number | null
  verifiedDomains: FederationConsentDomain[]
  // Populated for Declined / Expired / Failed.
  failureReason: string | null
  correlationId: string | null
}

export interface LinkFederationDomainRequest {
  verifiedDomainId: number
}
